/**
 * Unified P&L computation — single source of truth across all MCT components.
 *
 * Rules:
 *   1. If a live WebSocket tick is available, use it (most current, <1s delay)
 *   2. If no live tick, use the Kite API's pre-computed pnl field (accounts for
 *      corporate actions, splits, bonuses — matches Zerodha Console exactly)
 *   3. Last resort: compute from (ltp - avg_price) × qty
 *
 * Used by: Holdings.tsx, Positions.tsx, StoreSync.tsx, Dashboard.tsx, ActivePositions.tsx
 */

/** Compute P&L for a HOLDING (CNC delivery position held overnight) */
export function holdingPnl(
  avgPrice: number,
  qty: number,
  lastPrice: number,
  kitePnl: number | null | undefined,
  liveLtp: number | undefined,
): { ltp: number; invested: number; curVal: number; pnlAbs: number; pnlPct: number } {
  const ltp = liveLtp ?? lastPrice;
  const invested = avgPrice * qty;
  const curVal = ltp * qty;
  // Live tick → recompute (real-time). No live tick → use Kite's pnl (exact to Console).
  const pnlAbs = liveLtp !== undefined ? (curVal - invested) : (kitePnl ?? curVal - invested);
  const pnlPct = avgPrice > 0 ? ((ltp - avgPrice) / avgPrice) * 100 : 0;
  return { ltp, invested, curVal, pnlAbs, pnlPct };
}

/** Compute P&L for an OPEN POSITION (MIS intraday) */
export function positionPnl(
  avgPrice: number,
  quantity: number,
  apiLtp: number,
  unrealisedPnl: number,
  liveLtp: number | undefined,
): { ltp: number; pnl: number; changePct: number } {
  const ltp = liveLtp ?? apiLtp;
  // Match Kite: unrealised_pnl (includes partial closes) + delta from live tick
  const ltpDelta = liveLtp !== undefined && quantity !== 0
    ? (liveLtp - apiLtp) * quantity
    : 0;
  const pnl = unrealisedPnl + ltpDelta;
  const changePct = avgPrice > 0 ? ((ltp - avgPrice) / avgPrice) * 100 : 0;
  return { ltp, pnl, changePct };
}

/** Compute total unrealised P&L across all open positions (for store/dashboard) */
export function totalUnrealisedPnl(
  positions: Array<{ symbol: string; ltp: number; avg_price: number; quantity: number; unrealised_pnl: number }>,
  ltpMap: Record<string, number>,
): number {
  return positions
    .filter(p => p.quantity !== 0)
    .reduce((sum, pos) => {
      const { pnl } = positionPnl(pos.avg_price, pos.quantity, pos.ltp, pos.unrealised_pnl ?? 0, ltpMap[pos.symbol]);
      return sum + pnl;
    }, 0);
}

/** Day change P&L for holdings (today's move from previous close) */
export function holdingDayPnl(
  closePrice: number,
  ltp: number,
  qty: number,
): number {
  const prevClose = closePrice || ltp;
  return (ltp - prevClose) * qty;
}
